/**
 * PaymentSummary — "what this one charge covers".
 *
 * Same-day batch charge (Phase 2, PORTAL-SAME-DAY-APPOINTMENTS §4.5):
 * one card entry pays every checked visit's estimate plus the prior
 * balance. The cards on the left each show their own amount, but the
 * patient needs to see the composition of the single Stripe charge in
 * one place, next to the Pay button. This is that place: one row per
 * component (each checked appointment, then the past balance) and the
 * total the Pay button will charge.
 *
 * Pure presentation — `PayPage` computes the rows from the same state
 * that builds the request, so the summary and the request can't drift.
 */
import { Card, CardContent } from '@tensaw/design-system/layout';

import { format } from '../../lib/money';

export interface PaymentSummaryItem {
  /** Stable React key — `estimate-<appointment_id>` or `balance`. */
  key: string;
  /** Primary label, e.g. "Echo estimate" or "Past balance". */
  label: string;
  /** Secondary line, e.g. the clinic-local visit time or "2 visits". */
  detail?: string | null;
  amountCents: number;
}

interface PaymentSummaryProps {
  items: PaymentSummaryItem[];
  totalCents: number;
}

export function PaymentSummary({ items, totalCents }: PaymentSummaryProps) {
  if (items.length === 0) return null;
  return (
    <Card variant="subtle" data-testid="payment-summary">
      <CardContent className="p-6">
        <h2 className="text-sm font-semibold text-foreground">
          {items.length === 1 ? 'Paying today' : 'Pay together in one charge'}
        </h2>
        <ul className="mt-3 space-y-2 text-sm">
          {items.map((item) => (
            <li
              key={item.key}
              data-testid={`payment-summary-${item.key}`}
              className="flex items-start justify-between gap-3"
            >
              <div>
                <div className="text-foreground">{item.label}</div>
                {item.detail && (
                  <div className="text-xs text-muted-foreground">{item.detail}</div>
                )}
              </div>
              <div className="text-foreground tabular-nums">{format(item.amountCents)}</div>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-border pt-3">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Total today
          </div>
          <div
            data-testid="payment-summary-total"
            className="text-base font-semibold text-foreground tabular-nums"
          >
            {format(totalCents)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
